import React, { useState, useEffect, useContext, useRef, useMemo } from 'react'
import { toast } from 'react-toastify';
import { Search, X, Upload, Download, FileSpreadsheet } from 'lucide-react';
import * as XLSX from 'xlsx';
import moment from 'moment';
import Select from 'react-select';
import {
  getProductMaster,
  create_product_details,
  get_product_details,
  update_product_details,
  delete_product_details,
  adjustProductDetailsStock,
  bulkUploadProductDetails,
  get_product_detail_transection,
  revert_added_stock,
  distributeLoosePacks,
  getLoosePackInfo,
  getRawMaterials
} from "../../../Utility/Api";
import { WarehouseContext } from "../../../Utility/UserContext";
import { NaviGate } from "../../../Utility/function";

const extractPackagingWeightInGrams = (title) => {
  if (!title) return null;
  const t = title.trim();
  const mKg = t.match(/(\d+(?:\.\d+)?)\s*(?:kg|kgs|kilogram|kilograms)\b/i);
  if (mKg) return parseFloat(mKg[1]) * 1000;
  const mG = t.match(/(\d+(?:\.\d+)?)\s*(?:grams?|gm|g)\b/i);
  if (mG) return parseFloat(mG[1]);
  const mLead = t.match(/^(\d+(?:\.\d+)?)\b/);
  if (mLead) {
    const val = parseFloat(mLead[1]);
    if ([1, 2, 5].includes(val)) return val * 1000;
    return val;
  }
  return null;
};

const filterSelectStyles = {
  control: (provided, state) => ({
    ...provided,
    minHeight: "38px",
    height: "38px",
    fontSize: "14px",
    borderRadius: "0.375rem",
    borderColor: state.isFocused ? "#0d6efd" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)" : "none",
    backgroundColor: "#ffffff",
    cursor: "pointer",
    fontFamily: "Poppins, sans-serif",
    "&:hover": {
      borderColor: state.isFocused ? "#0d6efd" : "#ced4da"
    }
  }),
  valueContainer: (provided) => ({
    ...provided,
    height: "38px",
    padding: "0 10px",
    display: "flex",
    alignItems: "center"
  }),
  input: (provided) => ({
    ...provided,
    margin: "0px",
    padding: "0px",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif"
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: "38px"
  }),
  placeholder: (provided) => ({
    ...provided,
    color: "#6c757d",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif"
  }),
  singleValue: (provided) => ({
    ...provided,
    color: "#212529",
    fontSize: "14px",
    fontFamily: "Poppins, sans-serif"
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 9999
  }),
  menu: (provided) => ({
    ...provided,
    zIndex: 9999,
    fontSize: "14px",
    borderRadius: "0.375rem",
    boxShadow: "0 0.5rem 1rem rgba(0, 0, 0, 0.15)",
    fontFamily: "Poppins, sans-serif"
  }),
  option: (provided, state) => ({
    ...provided,
    fontSize: "13px",
    backgroundColor: state.isSelected
      ? "#0d6efd"
      : state.isFocused
      ? "#e9ecef"
      : "#ffffff",
    color: state.isSelected ? "#ffffff" : "#212529",
    cursor: "pointer",
    fontFamily: "Poppins, sans-serif",
    "&:active": {
      backgroundColor: "#0d6efd",
      color: "#ffffff"
    }
  })
};

const Pod_details = () => {
  const { showLoader, hideLoader, UserItem, shopItem, selectedWarehouse, warehouse } = useContext(WarehouseContext);

  // State management
  const [products, setProducts] = useState([]);
  const [productDetails, setProductDetails] = useState([]);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedDetail, setSelectedDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [filterProductId, setFilterProductId] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search query (300ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Stock Adjustment States
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustDetail, setAdjustDetail] = useState(null);
  const [adjustQty, setAdjustQty] = useState("");
  const [adjustType, setAdjustType] = useState("with_val");
  const [adjustAction, setAdjustAction] = useState("add");
  const [adjustPrice, setAdjustPrice] = useState("");
  const [adjustDate, setAdjustDate] = useState(moment().format("YYYY-MM-DD"));
  const [isAdjustSubmitting, setIsAdjustSubmitting] = useState(false);

  // Distribute Loose Packs States
  const [showDistributeModal, setShowDistributeModal] = useState(false);
  const [distributeDetail, setDistributeDetail] = useState(null);
  const [distributeDate, setDistributeDate] = useState(moment().format("YYYY-MM-DD"));
  const [distributeInfo, setDistributeInfo] = useState(null);
  const [distributeLoading, setDistributeLoading] = useState(false);
  const [isDistributeSubmitting, setIsDistributeSubmitting] = useState(false);
  const [distributionPackRows, setDistributionPackRows] = useState([]);
  const [packagingMaterialsList, setPackagingMaterialsList] = useState([]);

  // Bulk Upload States
  const [showBulkModal, setShowBulkModal] = useState(false);
  const [bulkDate, setBulkDate] = useState(moment().format("YYYY-MM-DD"));
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkFileName, setBulkFileName] = useState("");
  const [bulkParsedData, setBulkParsedData] = useState([]);
  const [isUploadingBulk, setIsUploadingBulk] = useState(false);

  // Report Modal States
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportProductDetail, setReportProductDetail] = useState(null);
  const [allReportTransactions, setAllReportTransactions] = useState([]);
  const [reportFilter, setReportFilter] = useState(""); // "" = All, "1" = Added, "0" = Taken
  const [reportLoading, setReportLoading] = useState(false);
  const [revertConfirmItem, setRevertConfirmItem] = useState(null);
  const [revertLoading, setRevertLoading] = useState(false);
  const [revertMode, setRevertMode] = useState("full"); // "full" or "selected"
  const [revertQty, setRevertQty] = useState("");

  const handleOpenRevertModal = (item) => {
    setRevertConfirmItem(item);
    setRevertMode("full");
    const rem = item.remaining != null ? item.remaining : item.qty;
    setRevertQty(rem != null ? String(rem) : "");
  };
  const reportPrintRef = useRef(null);

  const formatReportQty = (val, isFree) => {
    const num = parseFloat(val) || 0;
    if (isFree) return num.toFixed(3);
    return Number(num.toFixed(2)).toString();
  };

  const openReportModal = async (detail) => {
    setReportProductDetail(detail);
    setReportFilter("");
    setShowReportModal(true);
    fetchReportTransactions(detail.id);
  };

  const fetchReportTransactions = async (pd_id) => {
    setReportLoading(true);
    try {
      const response = await get_product_detail_transection("", 1, 10000, "", pd_id);
      if (response.data && response.data.data) {
        setAllReportTransactions(response.data.data);
      } else {
        setAllReportTransactions([]);
      }
    } catch (error) {
      console.error("Error fetching end product report transactions:", error);
      toast.error("Failed to load transaction report");
    } finally {
      setReportLoading(false);
    }
  };

  const handlePrintReport = () => {
    const printContent = reportPrintRef.current;
    if (!printContent) return;

    const isFreeSize = reportProductDetail?.pbox_code === "FREE SIZE / LOSE QTY";
    const packUnitLabel = isFreeSize ? "kg" : "pkts";

    const totalAdded = allReportTransactions
      .filter(t => String(t.add_taken) === "1")
      .reduce((acc, t) => acc + (parseFloat(t.qty) || 0), 0);

    const totalTaken = allReportTransactions
      .filter(t => String(t.add_taken) === "0")
      .reduce((acc, t) => acc + (parseFloat(t.qty) || 0), 0);

    const availableStockDisplay = isFreeSize
      ? `${parseFloat(reportProductDetail?.p_qty || 0).toFixed(3)} ${packUnitLabel}`
      : `${formatReportQty(reportProductDetail?.p_qty, isFreeSize)} ${packUnitLabel}${formatAvailableStock(reportProductDetail) ? ` (${formatAvailableStock(reportProductDetail)})` : ""}`;

    const printWindow = window.open("", "", "width=900,height=650");
    const prodTitle = products.find(p => (p.id || p.product_id)?.toString() === reportProductDetail?.p_id?.toString())?.title || `Product #${reportProductDetail?.p_id}`;
    const packDesc = reportProductDetail?.pbox_code === "FREE SIZE / LOSE QTY" ? "Lose Packs" : `${reportProductDetail?.p_weight} ${reportProductDetail?.pbox_unit}`;
    printWindow.document.write(`
      <html>
        <head>
          <title>End Product Date-Wise Report - ${prodTitle} (${packDesc})</title>
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css">
          <link rel="stylesheet" href="https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css">
          <style>
            body { font-family: 'Poppins', sans-serif; padding: 25px; color: #222; }
            .table { font-size: 12px; border-color: #dee2e6; }
            .badge { font-size: 11px; }
            h4 { font-weight: 700; color: #111; }
            .print-header { border-bottom: 2px solid #0d6efd; padding-bottom: 12px; margin-bottom: 16px; }
            .summary-box {
              border-radius: 6px;
              padding: 10px 14px;
              text-align: center;
            }
            .no-print { display: none !important; }
            @media print {
              body { padding: 12px; }
              .no-print { display: none !important; }
              * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
            }
          </style>
        </head>
        <body>
          <div class="print-header d-flex justify-content-between align-items-center">
            <div>
              <h4 class="m-0">ZTR ERP - End Product Date-Wise Stock Report</h4>
              <p class="text-muted m-0" style="font-size: 13px;">
                Product: <strong>${prodTitle}</strong> | Pack Size: <strong>${packDesc}</strong> | Code: ${reportProductDetail?.p_code || "N/A"}
              </p>
            </div>
            <div class="text-end">
              <div class="text-muted" style="font-size: 11px;">Generated on: ${new Date().toLocaleString("en-GB")}</div>
              ${reportFilter === "1" ? '<span class="badge bg-success mt-1">Showing: Stock Added (In)</span>' : reportFilter === "0" ? '<span class="badge bg-danger mt-1">Showing: Stock Taken (Out)</span>' : '<span class="badge bg-secondary mt-1">Showing: All Transactions</span>'}
            </div>
          </div>

          <!-- Summary Cards in Print -->
          <div class="row g-3 mb-3">
            <div class="col-4">
              <div class="summary-box" style="border: 1px solid #198754; background-color: #f0fdf4 !important;">
                <div style="font-size: 10px; font-weight: 700; color: #166534; text-transform: uppercase; letter-spacing: 0.5px;">Total Stock Added</div>
                <div style="font-size: 17px; font-weight: 700; color: #15803d; margin-top: 2px;">
                  + ${formatReportQty(totalAdded, isFreeSize)} <span style="font-size: 12px; font-weight: 500;">${packUnitLabel}</span>
                </div>
              </div>
            </div>
            <div class="col-4">
              <div class="summary-box" style="border: 1px solid #dc3545; background-color: #fef2f2 !important;">
                <div style="font-size: 10px; font-weight: 700; color: #991b1b; text-transform: uppercase; letter-spacing: 0.5px;">Total Stock Taken</div>
                <div style="font-size: 17px; font-weight: 700; color: #b91c1c; margin-top: 2px;">
                  - ${formatReportQty(totalTaken, isFreeSize)} <span style="font-size: 12px; font-weight: 500;">${packUnitLabel}</span>
                </div>
              </div>
            </div>
            <div class="col-4">
              <div class="summary-box" style="border: 1px solid #0d6efd; background-color: #eff6ff !important;">
                <div style="font-size: 10px; font-weight: 700; color: #1e40af; text-transform: uppercase; letter-spacing: 0.5px;">Current Available Stock</div>
                <div style="font-size: 17px; font-weight: 700; color: #1d4ed8; margin-top: 2px;">
                  ${availableStockDisplay}
                </div>
              </div>
            </div>
          </div>

          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 400);
  };

  const handleConfirmRevert = async () => {
    if (!revertConfirmItem) return;
    const maxAvailable = parseFloat(revertConfirmItem?.remaining != null ? revertConfirmItem.remaining : (revertConfirmItem?.qty || 0));
    const qtyToRevert = revertMode === "full" ? maxAvailable : parseFloat(revertQty);

    if (isNaN(qtyToRevert) || qtyToRevert <= 0) {
      toast.error("Please enter a valid revert quantity greater than 0");
      return;
    }
    if (qtyToRevert > maxAvailable + 0.0001) {
      toast.error(`Cannot revert more than available quantity (${maxAvailable})`);
      return;
    }

    setRevertLoading(true);
    try {
      const response = await revert_added_stock({
        transaction_id: revertConfirmItem.id,
        stock_type: "end_product",
        revert_qty: qtyToRevert,
        user_id: UserItem?.user_id || UserItem?.id
      });
      if (response.data && response.data.status === "success") {
        toast.success(response.data.message || "Stock addition reverted successfully");
        setRevertConfirmItem(null);
        if (reportProductDetail?.id) {
          fetchReportTransactions(reportProductDetail.id);
        }
        fetchProductDetails();
        try {
          const updatedDetailsRes = await get_product_details({ shop_id: shopItem?.shop_id });
          if (updatedDetailsRes.data && updatedDetailsRes.data.data) {
            const updated = updatedDetailsRes.data.data.find(d => d.id === reportProductDetail?.id);
            if (updated) {
              setReportProductDetail(updated);
            }
          }
        } catch (e) {
          console.error("Error refreshing detail stock:", e);
        }
      } else {
        toast.error(response.data?.message || "Failed to revert stock");
      }
    } catch (error) {
      console.error("Error reverting end product stock:", error);
      toast.error(error.response?.data?.message || "Error reverting stock");
    } finally {
      setRevertLoading(false);
    }
  };


  // Form data
  const [formData, setFormData] = useState({
    p_id: '',
    p_code: '',
    p_weight: '',
    // warehouse_id: selectedWarehouse?.id || '',
    branch_id: '',
    shop_id: shopItem?.shop_id || '',
    p_price: '',
    pbox_qty: '',
    pbox_unit: '',
    pbox_code: '',
    pbox_price: '',
    created_by: UserItem?.user_id || ''
  });

  // Form errors
  const [errors, setErrors] = useState({});

  // Fetch data on component mount
  useEffect(() => {
    fetchProducts();
    fetchProductDetails();
  }, []);

  // Fetch products list
  const fetchProducts = async () => {
    showLoader();
    try {
      const response = await getProductMaster();
      if (response.data && response.data.data) {
        const sortedProducts = [...response.data.data].sort((a, b) =>
          (a.title || "").localeCompare(b.title || "", undefined, { sensitivity: 'base' })
        );
        setProducts(sortedProducts);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      toast.error("Failed to fetch products");
    } finally {
      hideLoader();
    }
  };

  // Fetch product details
  const fetchProductDetails = async (page = 1, text = "") => {
    showLoader();
    try {
      const params = {
        shop_id: shopItem?.shop_id || "0",
        // ware_id: warehouse?.id || "1",
        page,
        search: text,
      };

      const response = await get_product_details(params);

      if (response.data && response.data.data) {
        setProductDetails(response.data.data);
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to fetch product details");
    } finally {
      hideLoader();
    }
  };


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Clear error when field is changed
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  // Handle product selection
  const handleProductSelect = (e) => {
    const productId = e.target.value;
    setFormData({
      ...formData,
      p_id: productId
    });

    // Find selected product
    const product = products.find(p => p.product_id.toString() === productId);
    setSelectedProduct(product);

    if (product) {
      // Pre-fill form with product data
      setFormData(prev => ({
        ...prev,
        p_id: productId,
        p_code: product.product_code || '',
        pbox_unit: product.unit || '',
        p_weight: isEditMode ? prev.p_weight : '',  // Keep weight if editing
        p_price: product.sales_price || '',
        pbox_qty: product.box_qty || '',
        pbox_code: product.box_barcode || '',
        pbox_price: product.box_sales || '',
        reorder_point: product.reorder_point || '',
      }));
    }
  };

  const handleEdit = (detail) => {
    setIsEditMode(true);
    setSelectedDetail(detail);

    setFormData({
      id: detail.id,
      p_id: detail.p_id || '',
      p_code: detail.p_code || '',
      p_weight: detail.p_weight || '',
      // warehouse_id: detail.warehouse_id || selectedWarehouse?.id || '',
      branch_id: detail.branch_id || '',
      shop_id: detail.shop_id || shopItem?.shop_id || '',
      p_price: detail.p_price || '',
      pbox_qty: detail.pbox_qty || '',
      pbox_unit: detail.pbox_unit || '',
      pbox_code: detail.pbox_code || '',
      pbox_price: detail.pbox_price || '',
      reorder_point: detail.reorder_point || '',
      created_by: detail.created_by || UserItem?.user_id || ''
    });

    const product = products.find(p => p.product_id.toString() === detail.p_id);
    setSelectedProduct(product);
    setShowDetailsModal(true);
  };

  const handleDeleteClick = (detail) => {
    setSelectedDetail(detail);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedDetail) return;

    showLoader();
    try {
      const response = await delete_product_details(selectedDetail.id);

      if (response.data && response.data.status === 'success') {
        toast.success("Product detail deleted successfully");
        setShowDeleteModal(false);
        fetchProductDetails(); // Refresh list
      } else {
        toast.error(response.data?.message || "Failed to delete product detail");
      }
    } catch (error) {
      console.error("Error deleting product detail:", error);
      toast.error(error?.response?.data?.message || "Error deleting product detail");
    } finally {
      hideLoader();
    }
  };

  const openAdjustModal = (detail) => {
    setAdjustDetail(detail);
    setAdjustQty("");
    setAdjustPrice(detail.p_price || "");
    setAdjustType("with_val");
    setAdjustAction("add");
    setAdjustDate(moment().format("YYYY-MM-DD"));
    setShowAdjustModal(true);
  };

  const handleAdjustSubmit = async (e) => {
    e.preventDefault();
    if (isAdjustSubmitting) return;

    const stockChangeKg = parseFloat(adjustQty);
    if (!stockChangeKg || isNaN(stockChangeKg) || stockChangeKg <= 0) {
      toast.error("Please enter a valid quantity greater than 0");
      return;
    }

    const packWeight = parseFloat(adjustDetail.p_weight || 0);
    const isLosePack = adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" || packWeight <= 0;

    // Calculate packets change from stock in KG
    let qtyChange = 0;
    if (isLosePack) {
      qtyChange = stockChangeKg;
    } else {
      const unit = (adjustDetail.pbox_unit || "").toLowerCase().trim();
      const isKg = unit.includes("kg") || unit.includes("kilo");
      const packWeightInKg = isKg ? packWeight : packWeight / 1000;
      qtyChange = packWeightInKg > 0 ? stockChangeKg / packWeightInKg : 0;
    }

    if (!qtyChange || qtyChange <= 0) {
      toast.error("Invalid quantity or pack weight for adjustment");
      return;
    }

    setIsAdjustSubmitting(true);
    showLoader();
    try {
      const payload = {
        pd_id: adjustDetail.id,
        qty: qtyChange,
        action: adjustAction,
        price: adjustPrice,
        is_value_adjustment: adjustType === "with_val",
        shop_id: shopItem?.id || shopItem?.shop_id || "",
        user_id: UserItem?.user_id || "1",
        date: adjustDate,
      };

      const response = await adjustProductDetailsStock(payload);
      if (response.data && response.data.status === "success") {
        toast.success("Stock adjusted successfully");
        setShowAdjustModal(false);
        setAdjustQty("");
        fetchProductDetails();
      } else {
        toast.error(response.data?.message || "Failed to adjust stock");
      }
    } catch (err) {
      console.error("Adjustment error", err);
      toast.error(err.response?.data?.message || "Failed to adjust stock");
    } finally {
      setIsAdjustSubmitting(false);
      hideLoader();
    }
  };

  // Open Distribute Loose Packs Modal
  const openDistributeModal = async (detail) => {
    setDistributeDetail(detail);
    setDistributeDate(moment().format("YYYY-MM-DD"));
    setShowDistributeModal(true);

    // 1. Immediately pre-populate pack sizes and stock from local state so modal is NEVER empty
    const localPacks = (productDetails || []).filter(
      p => String(p.p_id) === String(detail.p_id) &&
           p.pbox_code !== "FREE SIZE / LOSE QTY" &&
           p.p_code !== "FREE_SIZE" &&
           String(p.id) !== String(detail.id)
    );

    const initialPackSizes = localPacks.map(p => {
      let w_val = parseFloat(p.p_weight) || 1.0;
      let u_low = (p.pbox_unit || "").toLowerCase();
      let pkg = (u_low.includes("kg") || u_low.includes("kilo")) ? w_val : (w_val / 1000.0);
      return {
        id: p.id,
        p_code: p.p_code,
        p_weight: p.p_weight,
        pbox_unit: p.pbox_unit,
        p_price: parseFloat(p.p_price || 0),
        current_packets: parseFloat(p.p_qty || 0),
        packet_weight_kg: pkg
      };
    });

    const initialStockKg = parseFloat(detail.p_qty || 0);

    setDistributeInfo({
      product_id: detail.p_id,
      product_title: products.find(p => String(p.product_id) === String(detail.p_id))?.title || `Product #${detail.p_id}`,
      available_pmd_kg: initialStockKg,
      available_bulk_kg: 0,
      total_available_kg: initialStockKg,
      pack_sizes: initialPackSizes
    });

    setDistributionPackRows(
      initialPackSizes.map(ps => ({
        pd_id: ps.id,
        p_code: ps.p_code,
        pack_name: `${ps.p_weight} ${ps.pbox_unit}`,
        packet_weight_kg: ps.packet_weight_kg,
        current_packets: ps.current_packets,
        p_price: ps.p_price,
        packet_count: "",
        packaging_id: "",
        packaging_qty: ""
      }))
    );

    setDistributeLoading(true);
    try {
      const res = await getLoosePackInfo({
        pd_id: detail.id,
        p_id: detail.p_id,
        shop_id: shopItem?.shop_id || shopItem?.id || ""
      });

      if (res.data && res.data.status === "success" && res.data.data) {
        const srvData = res.data.data;
        setDistributeInfo(srvData);

        const serverPackSizes = srvData.pack_sizes || [];
        const srvMats = srvData.packaging_materials || [];
        if (serverPackSizes.length > 0) {
          setDistributionPackRows(
            serverPackSizes.map(ps => {
              const rowGrams = Math.round((parseFloat(ps.packet_weight_kg) || 0) * 1000);
              const matchedMat = srvMats.find(rm => {
                const matWeight = extractPackagingWeightInGrams(rm.title);
                return matWeight && Math.abs(matWeight - rowGrams) <= 1;
              });
              return {
                pd_id: ps.id,
                p_code: ps.p_code,
                pack_name: `${ps.p_weight} ${ps.pbox_unit}`,
                packet_weight_kg: ps.packet_weight_kg,
                current_packets: ps.current_packets,
                p_price: ps.p_price,
                packet_count: "",
                packaging_id: matchedMat ? matchedMat.id : "",
                packaging_qty: ""
              };
            })
          );
        }

        if (srvMats.length > 0) {
          setPackagingMaterialsList(srvMats);
        }
      }
    } catch (err) {
      console.warn("Could not load backend pack distribution info:", err);
      // We already populated initialPackSizes from local state, so only inform if no pack sizes at all
      if (initialPackSizes.length === 0) {
        toast.info("No configured packet sizes found for this product.");
      }
    } finally {
      // Also fetch packaging materials if not already loaded
      try {
        const rmRes = await getRawMaterials({ shop_id: shopItem?.shop_id || shopItem?.id || "" });
        if (rmRes.data && rmRes.data.data) {
          const pouches = rmRes.data.data.filter(rm => String(rm.type) === "1").map(rm => ({
            id: rm.id,
            title: rm.title,
            stock: parseFloat(rm.remaining_stock || rm.stock || 0),
            unit: rm.unit || rm.unit_id || "kg",
            pcs_per_kg: parseFloat(rm.pcs_per_kg || 0)
          }));
          if (pouches.length > 0) {
            setPackagingMaterialsList(pouches);
            setDistributionPackRows(prevRows =>
              prevRows.map(r => {
                if (r.packaging_id) return r;
                const rowGrams = Math.round((parseFloat(r.packet_weight_kg) || 0) * 1000);
                const matchedMat = pouches.find(rm => {
                  const matWeight = extractPackagingWeightInGrams(rm.title);
                  return matWeight && Math.abs(matWeight - rowGrams) <= 1;
                });
                return matchedMat ? { ...r, packaging_id: matchedMat.id } : r;
              })
            );
          }
        }
      } catch (e) {
        console.error("Error fetching raw packaging materials:", e);
      }
      setDistributeLoading(false);
    }
  };

  const handlePackCountChange = (index, value) => {
    const updated = [...distributionPackRows];
    updated[index].packet_count = value;

    // Auto-select matching packaging cover if not selected yet
    if (!updated[index].packaging_id && packagingMaterialsList.length > 0) {
      const rowGrams = Math.round((parseFloat(updated[index].packet_weight_kg) || 0) * 1000);
      const matched = packagingMaterialsList.find(rm => {
        const matWeight = extractPackagingWeightInGrams(rm.title);
        return matWeight && Math.abs(matWeight - rowGrams) <= 1;
      });
      if (matched) {
        updated[index].packaging_id = matched.id;
      }
    }

    if (updated[index].packaging_id && (!updated[index].packaging_qty || updated[index].packaging_qty === updated[index]._prev_count)) {
      updated[index].packaging_qty = value;
    }
    updated[index]._prev_count = value;
    setDistributionPackRows(updated);
  };

  const handlePackagingChange = (index, packagingId) => {
    const updated = [...distributionPackRows];
    updated[index].packaging_id = packagingId;
    if (packagingId && !updated[index].packaging_qty) {
      updated[index].packaging_qty = updated[index].packet_count || "";
    }
    setDistributionPackRows(updated);
  };

  const handlePackagingQtyChange = (index, value) => {
    const updated = [...distributionPackRows];
    updated[index].packaging_qty = value;
    setDistributionPackRows(updated);
  };

  const totalDistributedKg = useMemo(() => {
    return distributionPackRows.reduce((sum, row) => {
      const count = parseFloat(row.packet_count) || 0;
      const wt = parseFloat(row.packet_weight_kg) || 0;
      return sum + (count * wt);
    }, 0);
  }, [distributionPackRows]);

  const availableLooseStockKg = useMemo(() => {
    if (distributeInfo?.total_available_kg != null) {
      return parseFloat(distributeInfo.total_available_kg || 0);
    }
    return parseFloat(distributeDetail?.p_qty || 0);
  }, [distributeInfo, distributeDetail]);

  const handleDistributeSubmit = async (e) => {
    e.preventDefault();
    if (isDistributeSubmitting) return;

    if (totalDistributedKg <= 0) {
      toast.error("Please enter packet quantity for at least one pack size");
      return;
    }

    if (totalDistributedKg > availableLooseStockKg + 0.001) {
      toast.error(`Total weight to distribute (${totalDistributedKg.toFixed(3)} kg) exceeds available loose stock (${availableLooseStockKg.toFixed(3)} kg)`);
      return;
    }

    const validItems = distributionPackRows
      .filter(row => parseFloat(row.packet_count) > 0)
      .map(row => ({
        pd_id: row.pd_id,
        packet_count: parseFloat(row.packet_count),
        packaging_id: row.packaging_id || null,
        packaging_qty: parseFloat(row.packaging_qty || row.packet_count || 0)
      }));

    if (validItems.length === 0) {
      toast.error("No valid packet quantities entered");
      return;
    }

    setIsDistributeSubmitting(true);
    showLoader();
    try {
      const payload = {
        pd_id: distributeDetail.id,
        p_id: distributeDetail.p_id,
        distributions: validItems,
        date: distributeDate,
        shop_id: shopItem?.shop_id || shopItem?.id || "",
        branch_id: UserItem?.user_id || "1",
        warehouse_id: warehouse?.id || "1",
        user_id: UserItem?.user_id || "1"
      };

      const response = await distributeLoosePacks(payload);
      if (response.data && response.data.status === "success") {
        toast.success(response.data.message || "Loose stock distributed into packets successfully!");
        setShowDistributeModal(false);
        setDistributionPackRows([]);
        fetchProductDetails();
      } else {
        toast.error(response.data?.message || "Failed to distribute loose stock");
      }
    } catch (err) {
      console.error("Distribution error:", err);
      toast.error(err?.response?.data?.message || "Error distributing loose stock into packets");
    } finally {
      setIsDistributeSubmitting(false);
      hideLoader();
    }
  };

  // Download Sample Excel Template for Bulk Upload
  const downloadSampleTemplate = () => {
    const sampleData = [
      {
        "Product Name": "BLACK PEPPER KMT",
        "Product Code": "BP-10G",
        "Weight": 10,
        "Unit": "grams",
        "Packets (Stock Qty)": 100,
        "Selling Price (INR)": 3.4,
        "Costing (INR)": 49.39,
        "Reorder Point": 20,
        "Box Quantity": 50,
        "Box Price (INR)": 160
      },
      {
        "Product Name": "BLACK PEPPER KMT",
        "Product Code": "BP-50G",
        "Weight": 50,
        "Unit": "grams",
        "Packets (Stock Qty)": 50,
        "Selling Price (INR)": 14,
        "Costing (INR)": 49.39,
        "Reorder Point": 10,
        "Box Quantity": 20,
        "Box Price (INR)": 260
      },
      {
        "Product Name": "AFSAR 65",
        "Product Code": "AF-500G",
        "Weight": 500,
        "Unit": "grams",
        "Packets (Stock Qty)": 25,
        "Selling Price (INR)": 67.50,
        "Costing (INR)": 45.00,
        "Reorder Point": 5,
        "Box Quantity": 10,
        "Box Price (INR)": 650
      }
    ];

    const ws = XLSX.utils.json_to_sheet(sampleData);
    ws['!cols'] = [
      { wch: 22 },
      { wch: 15 },
      { wch: 10 },
      { wch: 10 },
      { wch: 20 },
      { wch: 18 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
      { wch: 15 },
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Product_Packs_Template");
    XLSX.writeFile(wb, "product_packs_bulk_upload_template.xlsx");
  };

  // Handle Bulk Excel File Upload & Parsing
  const handleBulkFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setBulkFileName(file.name);
    setBulkFile(file);

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const bstr = evt.target.result;
        const wb = XLSX.read(bstr, { type: "binary" });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        const rawData = XLSX.utils.sheet_to_json(ws, { defval: "" });

        if (!rawData || rawData.length === 0) {
          toast.error("The uploaded file is empty");
          setBulkParsedData([]);
          return;
        }

        const parsed = rawData.map((row) => {
          const productName = String(row["Product Name"] || row["product_name"] || row["Product"] || row["title"] || "").trim();
          const productCode = String(row["Product Code"] || row["product_code"] || row["p_code"] || "").trim();
          const weight = parseFloat(row["Weight"] || row["weight"] || row["size"] || row["p_weight"] || 0);
          const unit = String(row["Unit"] || row["unit"] || row["pbox_unit"] || "grams").trim().toLowerCase();
          const packets = parseFloat(row["Packets (Stock Qty)"] || row["Packets"] || row["packets"] || row["Stock"] || row["stock"] || row["qty"] || 0);
          const sellingPrice = parseFloat(row["Selling Price (INR)"] || row["Selling Price"] || row["Price (INR)"] || row["price"] || row["p_price"] || 0);
          const costing = parseFloat(row["Costing (INR)"] || row["Costing"] || row["costing"] || row["mfg_price"] || 0);
          const reorderPoint = row["Reorder Point"] || row["reorder_point"] || "";
          const boxQty = row["Box Quantity"] || row["box_qty"] || row["pbox_qty"] || "";
          const boxPrice = row["Box Price (INR)"] || row["Box Price"] || row["box_price"] || row["pbox_price"] || "";

          return {
            product_name: productName,
            product_code: productCode,
            p_weight: isNaN(weight) ? 0 : weight,
            pbox_unit: unit || "grams",
            qty: isNaN(packets) ? 0 : packets,
            p_price: isNaN(sellingPrice) ? 0 : sellingPrice,
            costing: isNaN(costing) ? 0 : costing,
            reorder_point: reorderPoint !== undefined ? String(reorderPoint) : "",
            pbox_qty: boxQty !== undefined ? String(boxQty) : "",
            pbox_price: boxPrice !== undefined ? String(boxPrice) : ""
          };
        }).filter(item => item.product_name);

        if (parsed.length === 0) {
          toast.error("No valid product rows found. Please ensure 'Product Name' column is filled.");
        } else {
          toast.success(`Successfully parsed ${parsed.length} items from Excel.`);
        }

        setBulkParsedData(parsed);
      } catch (error) {
        console.error("Error reading excel file", error);
        toast.error("Failed to parse file. Please upload a valid Excel or CSV file.");
        setBulkParsedData([]);
      }
    };

    reader.readAsBinaryString(file);
  };

  // Submit Bulk Upload
  const handleBulkSubmit = async () => {
    if (bulkParsedData.length === 0) {
      toast.error("No products to upload");
      return;
    }

    setIsUploadingBulk(true);
    showLoader();
    try {
      const payload = {
        shop_id: shopItem?.id || shopItem?.shop_id || "0",
        user_id: UserItem?.user_id || "1",
        date: bulkDate,
        items: bulkParsedData
      };

      const response = await bulkUploadProductDetails(payload);
      if (response.data && response.data.status === "success") {
        toast.success(response.data.message || "Bulk upload completed successfully!");
        setShowBulkModal(false);
        setBulkFile(null);
        setBulkFileName("");
        setBulkParsedData([]);
        fetchProductDetails();
        fetchProducts();
      } else {
        toast.error(response.data?.message || "Failed to upload bulk products");
      }
    } catch (err) {
      console.error("Bulk upload error", err);
      toast.error(err.response?.data?.message || "Error processing bulk upload");
    } finally {
      setIsUploadingBulk(false);
      hideLoader();
    }
  };



  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.p_id) newErrors.p_id = "Product selection is required";
    if (!formData.p_weight) newErrors.p_weight = "Weight is required";
    if (!formData.p_price) newErrors.p_price = "Price is required";
    if (!formData.pbox_unit) newErrors.pbox_unit = "Unit is required";
    // pbox_qty, pbox_code, pbox_price, reorder_point are optional

    // Check for duplicate weight and unit in the same product
    const targetPId = formData.p_id?.toString();
    const targetWeight = parseFloat(formData.p_weight);
    const targetUnit = formData.pbox_unit?.trim().toLowerCase();

    if (targetPId && !isNaN(targetWeight) && targetUnit) {
      const isDuplicate = productDetails.some(detail => {
        // Skip comparing with itself when in edit mode
        if (isEditMode && detail.id === formData.id) return false;

        // Skip check for "FREE SIZE / LOSE QTY" default details
        if (detail.pbox_code === "FREE SIZE / LOSE QTY") return false;

        const detPId = detail.p_id?.toString();
        const detWeight = parseFloat(detail.p_weight);
        const detUnit = detail.pbox_unit?.trim().toLowerCase();

        return (
          detPId === targetPId &&
          !isNaN(detWeight) &&
          Math.abs(detWeight - targetWeight) < 0.0001 &&
          detUnit === targetUnit
        );
      });

      if (isDuplicate) {
        newErrors.p_weight = "This product weight already exists.";
        newErrors.pbox_unit = "This product weight already exists.";
        toast.error(`Product weight pack '${formData.p_weight} ${formData.pbox_unit}' already exists for this product.`);
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    showLoader();

    try {
      let response;

      if (isEditMode) {
        response = await update_product_details(formData.id, formData);
        // response = await (formData);
      } else {
        response = await create_product_details(formData);
      }

      if (response.data && response.data.status === 'success') {
        toast.success(isEditMode ? "Product details updated successfully" : "Product details added successfully");
        resetForm();
        setShowDetailsModal(false);
        fetchProductDetails();
      } else {
        toast.error(response.data?.message || (isEditMode ? "Failed to update product details" : "Failed to add product details"));
      }
    } catch (error) {
      console.error(isEditMode ? "Error updating product details:" : "Error adding product details:", error);
      toast.error(error?.response?.data?.message || (isEditMode ? "Error updating product details" : "Error adding product details"));
    } finally {
      setLoading(false);
      hideLoader();
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      p_id: '',
      p_code: '',
      p_weight: '',
      // warehouse_id: selectedWarehouse?.id || '',
      branch_id: '',
      shop_id: shopItem?.shop_id || '',
      p_price: '',
      pbox_qty: '',
      pbox_unit: '',
      pbox_code: '',
      pbox_price: '',
      reorder_point: '',
      created_by: UserItem?.user_id || ''
    });
    setSelectedProduct(null);
    setSelectedDetail(null);
    setIsEditMode(false);
    setErrors({});
  };

  // Format date for display
  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const formatAvailableStock = (detail) => {
    if (!detail) return "0 grams";
    const isFreeSize = detail.pbox_code === "FREE SIZE / LOSE QTY" || detail.p_code === "FREE_SIZE";
    const qty = parseFloat(detail.p_qty || 0);
    const weight = parseFloat(detail.p_weight || 0);
    const unit = (detail.pbox_unit || (isFreeSize ? "kg" : "")).toLowerCase();

    if (qty <= 0 || (weight <= 0 && !isFreeSize)) {
      if (isFreeSize || unit.includes("kg") || unit.includes("kilo")) {
        return "0.000 kg";
      }
      return "0 grams";
    }

    if (isFreeSize || unit.includes("kg") || unit.includes("kilo")) {
      const totalKg = isFreeSize ? qty : (qty * weight);
      return `${totalKg.toFixed(3)} kg`;
    } else if (unit.includes("gram") || unit === "g") {
      const totalGrams = qty * weight;
      if (totalGrams >= 1000) {
        const totalKg = totalGrams / 1000;
        return `${totalKg.toFixed(3)} kg`;
      } else {
        return `${Number.isInteger(totalGrams) ? totalGrams : totalGrams.toFixed(2)} grams`;
      }
    } else {
      const total = qty * weight;
      return `${total.toFixed(3)} ${detail.pbox_unit || ""}`;
    }
  };

  const sortedDetails = [...productDetails].sort((a, b) => {
    const productA = products.find(p => p.product_id?.toString() === a.p_id?.toString());
    const productB = products.find(p => p.product_id?.toString() === b.p_id?.toString());
    const titleA = (productA?.title || a.product_title || `Product #${a.p_id}`).trim();
    const titleB = (productB?.title || b.product_title || `Product #${b.p_id}`).trim();
    const cmp = titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
    if (cmp !== 0) return cmp;

    const isFreeA = a.pbox_code === "FREE SIZE / LOSE QTY" || a.p_code === "FREE_SIZE";
    const isFreeB = b.pbox_code === "FREE SIZE / LOSE QTY" || b.p_code === "FREE_SIZE";
    if (isFreeA && !isFreeB) return 1;
    if (!isFreeA && isFreeB) return -1;
    return parseFloat(a.p_weight || 0) - parseFloat(b.p_weight || 0);
  });

  const productFilterOptions = useMemo(() => [
    { value: "", label: "Filter by Product (All)" },
    ...products.map(p => ({
      value: (p.product_id || p.id)?.toString(),
      label: p.title || `Product #${p.product_id || p.id}`
    }))
  ], [products]);

  const currentFilterOption = useMemo(() => {
    if (!filterProductId) return productFilterOptions[0];
    return productFilterOptions.find(opt => opt.value === filterProductId.toString()) || { value: filterProductId, label: "Selected Product" };
  }, [filterProductId, productFilterOptions]);

  const filteredDetails = sortedDetails.filter(detail => {
    // Dropdown filter by Product
    if (filterProductId && (!detail.p_id || detail.p_id.toString() !== filterProductId.toString())) {
      return false;
    }

    // Debounced Search filter
    if (debouncedSearch.trim()) {
      const query = debouncedSearch.toLowerCase().trim();
      const productObj = products.find(p => p.product_id.toString() === detail.p_id?.toString());
      const productName = (productObj?.title || `product #${detail.p_id}`).toLowerCase();
      const productCode = (detail.p_code || "").toLowerCase();
      const weightText = (detail.pbox_code === "FREE SIZE / LOSE QTY" ? "lose packs" : `${detail.p_weight} ${detail.pbox_unit}`).toLowerCase();
      const priceText = (detail.p_price || "").toString().toLowerCase();
      const costingText = (detail.costing || detail.mfg_price || "").toString().toLowerCase();
      const reorderText = (detail.reorder_point || "").toString().toLowerCase();

      const matches =
        productName.includes(query) ||
        productCode.includes(query) ||
        weightText.includes(query) ||
        priceText.includes(query) ||
        costingText.includes(query) ||
        reorderText.includes(query);

      if (!matches) return false;
    }

    return true;
  });

  return (
    <div className="page-wrappers">
      <div className="page-content">
        <NaviGate />

        <div className="mb-0 d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h6 className="mb-0 text-uppercase" style={{ fontFamily: "poppins" }}>
            Product Packs
          </h6>
          <div className="d-flex align-items-center gap-2 flex-wrap">
            {/* Search Input with Debounce (Hidden as requested) */}
            <div className="position-relative" style={{ minWidth: "250px", display: "none" }}>
              <input
                type="text"
                className="form-control ps-5"
                placeholder="Search product"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <Search
                size={18}
                className="position-absolute top-50 start-0 translate-middle-y ms-3 text-secondary"
                style={{ pointerEvents: "none" }}
              />
              {searchQuery && (
                <button
                  type="button"
                  className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-1 p-1 text-secondary text-decoration-none"
                  onClick={() => setSearchQuery("")}
                >
                  <X size={16} />
                </button>
              )}
            </div>

            {/* Filter by Product Dropdown with type search */}
            <div style={{ minWidth: "240px", maxWidth: "280px" }}>
              <Select
                options={productFilterOptions}
                value={currentFilterOption}
                onChange={(selected) => setFilterProductId(selected ? selected.value : "")}
                isSearchable={true}
                isClearable={Boolean(filterProductId)}
                placeholder="Filter by Product (All)"
                styles={filterSelectStyles}
                menuPortalTarget={typeof document !== "undefined" ? document.body : null}
              />
            </div>

            <button
              type="button"
              className="btn btn-outline-primary text-nowrap d-flex align-items-center gap-1"
              onClick={() => {
                setBulkFile(null);
                setBulkFileName("");
                setBulkParsedData([]);
                setBulkDate(moment().format("YYYY-MM-DD"));
                setShowBulkModal(true);
              }}
            >
              <Upload size={16} /> Bulk Upload
            </button>

            <button
              type="button"
              className="btn btn-primary text-nowrap"
              onClick={() => {
                resetForm(); // Clear form before opening
                setShowDetailsModal(true);
              }}
            >
              Add Product Packs
            </button>
          </div>
        </div>
        <hr className="mt-1" />

        {/* Product Details Table */}
        <div className="card">
          <div className="card-body">
            <div className="table-responsive">
              <table className="table table-sm table-striped table-bordered align-middle text-nowrap mb-0">
                <thead>
                  <tr className="align-middle">
                    <th style={{ width: "40px" }} className="text-center">No</th>
                    <th>Product Code</th>
                    <th>Product</th>
                    <th>Weight</th>
                    <th>Packets</th>
                    <th>Avl Stock</th>
                    <th>Price (INR)</th>
                    <th>Costing (INR)</th>
                    {/* <th>Box Qty</th> */}
                    {/* <th>Box Unit</th> */}
                    {/* <th>Box Code</th> */}
                    {/* <th>Box Price (INR)</th> */}
                    <th>Reorder Point</th>
                    <th className="text-center" style={{ minWidth: "140px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDetails.length > 0 ? (
                    filteredDetails.map((detail, index) => (
                      <tr key={detail.id}>
                        <td className="text-center">{index + 1}</td>
                        <td>{detail.p_code}</td>
                        <td style={{ maxWidth: "200px", whiteSpace: "normal", overflowWrap: "break-word" }}>
                          {products.find(p => p.product_id.toString() === detail.p_id)?.title ||
                            `Product #${detail.p_id}`}
                        </td>
                        <td>
                          {detail.pbox_code === "FREE SIZE / LOSE QTY" ? "Lose Packs" : `${detail.p_weight} ${detail.pbox_unit}`}
                        </td>
                        <td>
                          {detail.pbox_code === "FREE SIZE / LOSE QTY" || detail.p_code === "FREE_SIZE"
                            ? parseFloat(detail.p_qty || 0).toFixed(3)
                            : (detail.p_qty || 0)}
                        </td>
                        <td>{formatAvailableStock(detail)}</td>
                        <td>₹{detail.p_price}</td>
                        <td>₹{parseFloat(detail.costing || detail.mfg_price || 0).toFixed(2)}</td>
                        {/* <td>{detail.pbox_qty}</td> */}
                        {/* <td>{detail.pbox_unit}</td> */}
                        {/* <td>{detail.pbox_code}</td> */}
                        {/* <td>{detail.pbox_price}</td> */}
                        <td>{detail.reorder_point}</td>
                        <td style={{ minWidth: "140px" }}>
                          {detail.pbox_code === "FREE SIZE / LOSE QTY" ? (
                            <div className="d-flex flex-column align-items-center gap-1 justify-content-center py-1">
                              <div className="d-flex align-items-center gap-2 justify-content-center">
                                <span className="badge bg-secondary" style={{ fontSize: "10.5px" }}>System Default</span>
                                <i
                                  className="bi bi-file-earmark-text text-primary"
                                  onClick={() => openReportModal(detail)}
                                  style={{ cursor: "pointer", fontSize: "16px", verticalAlign: "middle" }}
                                  title="Date-wise Stock Report"
                                ></i>
                                <img
                                  src="/assets/images/icons/transfer-switch.png"
                                  onClick={() => openAdjustModal(detail)}
                                  height="15px"
                                  alt="Adjust Stock"
                                  style={{ cursor: "pointer" }}
                                  title="Adjust Stock"
                                />
                              </div>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-success d-inline-flex align-items-center justify-content-center gap-1 py-0 px-2 shadow-sm"
                                style={{ fontSize: "11px", height: "23px", fontWeight: "600", borderRadius: "4px" }}
                                onClick={() => openDistributeModal(detail)}
                                title="Distribute in Packets"
                              >
                                <i className="bi bi-boxes" style={{ fontSize: "12px" }}></i>
                                <span>Distribute</span>
                              </button>
                            </div>
                          ) : (
                            <div className="d-flex align-items-center gap-2 justify-content-center py-1">
                              <i
                                className="bi bi-file-earmark-text text-primary"
                                onClick={() => openReportModal(detail)}
                                style={{ cursor: "pointer", fontSize: "16px", verticalAlign: "middle" }}
                                title="Date-wise Stock Report"
                              ></i>
                              <img
                                src="/assets/images/icons/edit.png"
                                onClick={() => handleEdit(detail)}
                                height="15px"
                                alt="Edit"
                                style={{ cursor: "pointer" }}
                                title="Edit"
                              />
                              <img
                                src="/assets/images/icons/transfer-switch.png"
                                onClick={() => openAdjustModal(detail)}
                                height="15px"
                                alt="Adjust Stock"
                                style={{ cursor: "pointer" }}
                                title="Adjust Stock"
                              />
                              <img
                                src="/assets/images/icons/delete.png"
                                onClick={() => handleDeleteClick(detail)}
                                height="15px"
                                alt="Delete"
                                style={{ cursor: "pointer" }}
                                title="Delete"
                              />
                            </div>
                          )}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="10" className="text-center">
                        No product details found
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Add/Edit Details Modal */}
        {showDetailsModal && (
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-lg">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">{isEditMode ? 'Edit Product Packs' : 'Add Product Packs'}</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowDetailsModal(false);
                      resetForm();
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleSubmit}>
                    <div className="row g-3">
                      {/* Product Selection */}
                      <div className="col-md-6"  >
                        <label className="form-label">Select Product <span className="text-danger">*</span></label>
                        <select
                          name="p_id"
                          value={formData.p_id}
                          onChange={handleProductSelect}
                          className={`form-control ${errors.p_id ? 'is-invalid' : ''}`}
                          disabled={isEditMode}
                        >
                          <option value="">Select Product</option>
                          {products.map(product => (
                            <option key={product.product_id} value={product.product_id}>
                              {product.title}
                            </option>
                          ))}
                        </select>
                        {errors.p_id && <div className="invalid-feedback">{errors.p_id}</div>}
                      </div>
                      {/* Product Code */}
                      <div className="col-md-6">
                        <label className="form-label">Product Code</label>
                        <input
                          type="text"
                          name="p_code"
                          value={formData.p_code}
                          onChange={handleInputChange}
                          className="form-control"

                        />
                      </div>

                      <div className="row mx-1 mt-1 mb-2">


                        {/* Product Weight */}
                        <div className="col-md-6 mb-1">
                          <label className="form-label">P_Weight <span className="text-danger">*</span></label>
                          <input
                            type="text"
                            name="p_weight"
                            value={formData.p_weight}
                            onChange={handleInputChange}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                              const parts = e.target.value.split('.');
                              if (parts.length > 2) {
                                e.target.value = parts[0] + '.' + parts.slice(1).join('');
                              }
                              if (parts[1] && parts[1].length > 3) {
                                e.target.value = parts[0] + '.' + parts[1].substring(0, 3);
                              }
                            }}
                            inputMode='decimal'
                            pattern="[0-9]*\.?[0-9]*"
                            placeholder="0.00"
                            className={`form-control ${errors.p_weight ? 'is-invalid' : ''}`}
                          />
                          {errors.p_weight && <div className="invalid-feedback">{errors.p_weight}</div>}
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Unit <span className="text-danger">*</span></label>
                          <select
                            name="pbox_unit"
                            value={formData.pbox_unit}
                            onChange={handleInputChange}
                            className={`form-control ${errors.pbox_unit ? 'is-invalid' : ''}`}
                          >
                            <option value="">Select Unit</option>
                            <option value="kilograms">Kilograms</option>
                            <option value="grams">Grams</option>
                            {/* <option value="liters">Liters</option>
                            <option value="milliliters">Milliliters</option>
                            <option value="pcs">Pieces</option> */}
                          </select>
                          {errors.pbox_unit && <div className="invalid-feedback">{errors.pbox_unit}</div>}
                        </div>
                        {/* Product Price */}
                        <div className="col-md-6 mb-1">
                          <label className="form-label">Price Per Unit (INR) <span className="text-danger">*</span></label>
                          <input
                            name="p_price"
                            value={formData.p_price}
                            onChange={handleInputChange}
                            onInput={(e) => {
                              e.target.value = e.target.value.replace(/[^0-9.]/g, '');
                              const parts = e.target.value.split('.');
                              if (parts.length > 2) {
                                e.target.value = parts[0] + '.' + parts.slice(1).join('');
                              }
                              if (parts[1] && parts[1].length > 3) {
                                e.target.value = parts[0] + '.' + parts[1].substring(0, 3);
                              }
                            }}
                            inputMode='decimal'
                            pattern="[0-9]*\.?[0-9]*"
                            placeholder="0.00"
                            className={`form-control ${errors.p_price ? 'is-invalid' : ''}`}
                            min="0"
                            step="0.01"
                          />
                          {errors.p_price && <div className="invalid-feedback">{errors.p_price}</div>}
                        </div>
                      </div>

                      {/* Box Quantity */}
                      <div className="row mx-1 pt-3">

                        <div className="col-md-6">
                          <label className="form-label">Box Quantity</label>
                          <input
                            type="number"
                            name="pbox_qty"
                            value={formData.pbox_qty}
                            onChange={handleInputChange}
                            className="form-control"
                            min="1"
                          />
                        </div>
                        {/* Box Unit */}



                        {/* Box Code */}
                        <div className="col-md-6 mb-2">
                          <label className="form-label">Box Code</label>
                          <input
                            type="text"
                            name="pbox_code"
                            value={formData.pbox_code}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>

                        {/* Box Price */}
                        <div className="col-md-6 mb-2">
                          <label className="form-label">Box Price (INR)</label>
                          <input
                            type="number"
                            name="pbox_price"
                            value={formData.pbox_price}
                            onChange={handleInputChange}
                            className="form-control"
                            min="0"
                            step="0.01"
                          />
                        </div>

                        <div className="col-md-6">
                          <label className="form-label">Reorder Point</label>
                          <input
                            type="text"
                            name="reorder_point"
                            value={formData.reorder_point}
                            onChange={handleInputChange}
                            className="form-control"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="text-end mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        onClick={() => {
                          setShowDetailsModal(false);
                          resetForm();
                        }}
                      >
                        Cancel
                      </button>
                      <button type="submit" className="btn btn-primary" disabled={loading}>
                        {loading ? 'Saving...' : isEditMode ? 'Update Details' : 'Save Details'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && selectedDetail && (
          <div className="modal fade show" style={{ display: "block" }} tabIndex="-1">
            <div className="modal-dialog modal-md">
              <div className="modal-content">

                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setShowDeleteModal(false)}
                  ></button>
                </div>

                <div className="modal-body text-center">
                  <i className="bi bi-exclamation-triangle text-danger fs-1 d-block mb-3"></i>

                  {/* PRODUCT DETAIL LINE */}
                  <p className="fw-bold">
                    {products.find(p => p.product_id.toString() === selectedDetail.p_id)?.title}
                    {" — "}
                    {selectedDetail.pbox_code === "FREE SIZE / LOSE QTY" || selectedDetail.p_code === "FREE_SIZE"
                      ? `Lose Packs Remaining QTY: ${parseFloat(selectedDetail.p_qty || 0).toFixed(3)} kg`
                      : `${selectedDetail.p_weight} ${selectedDetail.pbox_unit} Remaining QTY ${selectedDetail.p_qty || 0}`}
                  </p>

                  <p className="mt-2">
                    Are you sure you want to delete this item?
                    <br />
                    <small className="text-danger">This action cannot be undone.</small>
                  </p>
                </div>

                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setShowDeleteModal(false)}
                  >
                    Cancel
                  </button>

                  <button type="button" className="btn btn-danger" onClick={handleDelete}>
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Stock Adjustment Modal */}
        {showAdjustModal && adjustDetail && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-md modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Stock Adjustment</h5>
                  <button
                    type="button"
                    className="btn-close"
                    disabled={isAdjustSubmitting}
                    onClick={() => {
                      if (isAdjustSubmitting) return;
                      setShowAdjustModal(false);
                      setAdjustQty("");
                    }}
                  ></button>
                </div>
                <div className="modal-body">
                  <form onSubmit={handleAdjustSubmit}>
                    <div className="p-3 bg-light rounded mb-3">
                      <p className="mb-1">
                        Product: <strong>{products.find(p => p.product_id.toString() === adjustDetail.p_id.toString())?.title || `Product #${adjustDetail.p_id}`}</strong>
                      </p>
                      <p className="mb-1">
                        Pack Weight: <strong>{adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" ? "Lose Packs" : `${adjustDetail.p_weight} ${adjustDetail.pbox_unit}`}</strong>
                      </p>
                      <p className="mb-1">
                        {adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" || adjustDetail.p_code === "FREE_SIZE" ? (
                          <>Current Stock: <strong>{parseFloat(adjustDetail.p_qty || 0).toFixed(3)} kg</strong></>
                        ) : (
                          <>Current Packets: <strong>{adjustDetail.p_qty || 0}</strong></>
                        )}
                      </p>
                      <p className="mb-0">
                        Current Available Stock: <strong>{formatAvailableStock(adjustDetail)}</strong>
                      </p>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Adjustment Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        value={adjustDate}
                        onChange={(e) => setAdjustDate(e.target.value)}
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Adjustment Action <span className="text-danger">*</span></label>
                      <div className="d-flex gap-4">
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="adjustAction"
                            id="actionAdd"
                            value="add"
                            checked={adjustAction === "add"}
                            onChange={(e) => setAdjustAction(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="actionAdd">
                            Add Stock (UP)
                          </label>
                        </div>
                        <div className="form-check">
                          <input
                            className="form-check-input"
                            type="radio"
                            name="adjustAction"
                            id="actionDeduct"
                            value="deduct"
                            checked={adjustAction === "deduct"}
                            onChange={(e) => setAdjustAction(e.target.value)}
                          />
                          <label className="form-check-label" htmlFor="actionDeduct">
                            Deduct Stock (DOWN)
                          </label>
                        </div>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="form-label">
                        Stock to Adjust (kg) <span className="text-danger">*</span>
                      </label>
                      <input
                        type="number"
                        step="any"
                        min="0.001"
                        className="form-control"
                        value={adjustQty}
                        onChange={(e) => setAdjustQty(e.target.value)}
                        placeholder="Enter stock in kg"
                        required
                      />
                      {adjustQty && !isNaN(adjustQty) && parseFloat(adjustQty) > 0 && (
                        <div className="form-text text-primary mt-1 font-weight-1">
                          {(() => {
                            const stockChangeKg = parseFloat(adjustQty);
                            const packWeight = parseFloat(adjustDetail.p_weight || 0);
                            const isLosePack = adjustDetail.pbox_code === "FREE SIZE / LOSE QTY" || packWeight <= 0;
                            if (isLosePack) {
                              return `Equivalent change: ${stockChangeKg.toFixed(3)} kg`;
                            } else {
                              const unit = (adjustDetail.pbox_unit || "").toLowerCase().trim();
                              const isKg = unit.includes("kg") || unit.includes("kilo");
                              const packWeightInKg = isKg ? packWeight : packWeight / 1000;
                              const packets = packWeightInKg > 0 ? stockChangeKg / packWeightInKg : 0;
                              return `Equivalent change: ${packets.toFixed(2)} packets (calculated based on pack weight of ${adjustDetail.p_weight} ${adjustDetail.pbox_unit || "grams"})`;
                            }
                          })()}
                        </div>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Price Per Unit (INR) <span className="text-danger">*</span></label>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        className="form-control"
                        value={adjustPrice}
                        onChange={(e) => setAdjustPrice(e.target.value)}
                        placeholder="Enter price per unit for adjustment"
                        required
                      />
                    </div>

                    <div className="mb-3">
                      <label className="form-label">Adjustment Type <span className="text-danger">*</span></label>
                      <select
                        className="form-select"
                        value={adjustType}
                        onChange={(e) => setAdjustType(e.target.value)}
                      >
                        <option value="with_val">With Value (Reflects in Trial Balance)</option>
                        <option value="without_val">Without Value (No Ledger Entry)</option>
                      </select>
                    </div>

                    <div className="text-end mt-4">
                      <button
                        type="button"
                        className="btn btn-secondary me-2"
                        disabled={isAdjustSubmitting}
                        onClick={() => {
                          if (isAdjustSubmitting) return;
                          setShowAdjustModal(false);
                          setAdjustQty("");
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isAdjustSubmitting}
                      >
                        {isAdjustSubmitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (
                          "Save Adjustment"
                        )}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Distribute Loose Stock into Packets Modal */}
        {showDistributeModal && distributeDetail && (
          <div
            className="modal fade show"
            style={{ display: "block", backgroundColor: "rgba(0,0,0,0.55)", zIndex: 1050 }}
            tabIndex="-1"
            onClick={(e) => {
              if (e.target === e.currentTarget && !isDistributeSubmitting) {
                setShowDistributeModal(false);
              }
            }}
          >
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content shadow-lg border-0" style={{ borderRadius: "12px", overflow: "hidden" }}>
                {/* Modal Header */}
                <div
                  className="modal-header py-3 px-4 d-flex justify-content-between align-items-center"
                  style={{
                    backgroundColor: "#ffffff",
                    borderBottom: "1px solid #e2e8f0"
                  }}
                >
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                      style={{ width: "36px", height: "36px", backgroundColor: "#198754" }}
                    >
                      <i className="bi bi-boxes fs-5"></i>
                    </div>
                    <div>
                      <h5 className="modal-title mb-0 fw-bold fs-6" style={{ color: "#0f172a" }}>
                        Distribute Loose Stock into Packets
                      </h5>
                      <small className="text-muted" style={{ fontSize: "12px" }}>
                        Convert loose powder into packaged stock
                      </small>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn btn-sm btn-light border rounded-circle d-flex align-items-center justify-content-center shadow-none"
                    style={{
                      width: "32px",
                      height: "32px",
                      cursor: "pointer",
                      backgroundColor: "#f1f5f9",
                      borderColor: "#cbd5e1"
                    }}
                    disabled={isDistributeSubmitting}
                    onClick={() => setShowDistributeModal(false)}
                    title="Close"
                    aria-label="Close"
                  >
                    <i className="bi bi-x-lg text-dark" style={{ fontSize: "14px", fontWeight: "bold" }}></i>
                  </button>
                </div>

                {/* Modal Body */}
                <div className="modal-body p-4 bg-light">
                  {distributeLoading ? (
                    <div className="text-center py-5">
                      <div className="spinner-border text-primary" role="status"></div>
                      <p className="mt-2 text-muted">Loading product pack details...</p>
                    </div>
                  ) : (
                    <form onSubmit={handleDistributeSubmit}>
                      {/* Product Info Card */}
                      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "10px", backgroundColor: "#ffffff" }}>
                        <div className="card-body p-3">
                          <div className="row g-3 align-items-center">
                            <div className="col-md-5">
                              <span className="text-muted small d-block mb-1">Product</span>
                              <h6 className="mb-0 fw-bold text-dark">
                                {distributeInfo?.product_title ||
                                  products.find(p => String(p.product_id) === String(distributeDetail.p_id))?.title ||
                                  `Product #${distributeDetail.p_id}`}
                              </h6>
                              <span className="badge bg-secondary-subtle text-secondary border mt-1" style={{ fontSize: "11px" }}>
                                Code: {distributeDetail.p_code || "FREE_SIZE"}
                              </span>
                            </div>

                            <div className="col-md-4">
                              <span className="text-muted small d-block mb-1">Available Loose Stock</span>
                              <div className="d-flex align-items-center gap-2">
                                <span className={`badge fs-6 px-3 py-1.5 border ${availableLooseStockKg > 0 ? "bg-success-subtle text-success border-success" : "bg-warning-subtle text-warning border-warning"}`}>
                                  {availableLooseStockKg.toFixed(3)} kg
                                </span>
                              </div>
                            </div>

                            <div className="col-md-3">
                              <label className="form-label text-muted small mb-1">Distribution Date</label>
                              <input
                                type="date"
                                className="form-control form-control-sm"
                                value={distributeDate}
                                onChange={(e) => setDistributeDate(e.target.value)}
                                required
                              />
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Zero Stock Warning */}
                      {availableLooseStockKg <= 0 && (
                        <div className="alert alert-warning d-flex align-items-center gap-2 py-2 px-3 mb-3 border-warning" style={{ borderRadius: "8px" }}>
                          <i className="bi bi-exclamation-triangle-fill fs-5 text-warning flex-shrink-0"></i>
                          <div className="small">
                            <strong>Zero Loose Stock Available:</strong> Current loose stock is <strong>0.000 kg</strong>. You need available loose stock to distribute into packets. You can add loose stock via <strong>Adjust Stock</strong>.
                          </div>
                        </div>
                      )}

                      {/* Pack Distribution Table */}
                      <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "10px", backgroundColor: "#ffffff" }}>
                        <div className="card-header bg-white py-2 px-3 border-bottom d-flex justify-content-between align-items-center">
                          <h6 className="mb-0 fw-bold text-dark small d-flex align-items-center gap-2">
                            <i className="bi bi-box-seam text-primary"></i> Target Packet Sizes
                          </h6>
                          <span className="text-muted small">
                            {distributionPackRows.length} pack size{distributionPackRows.length === 1 ? "" : "s"} configured
                          </span>
                        </div>

                        <div className="card-body p-0">
                          {distributionPackRows.length === 0 ? (
                            <div className="p-4 text-center">
                              <i className="bi bi-info-circle text-muted fs-3 mb-2 d-block"></i>
                              <p className="text-muted mb-2">No packet sizes configured for this product yet.</p>
                              <small className="text-secondary d-block mb-3">
                                Please close this modal and click <strong>"Add Product Packs"</strong> to create pack sizes (e.g., 50g, 100g, 500g) first.
                              </small>
                              <button
                                type="button"
                                className="btn btn-secondary btn-sm px-4"
                                onClick={() => setShowDistributeModal(false)}
                              >
                                <i className="bi bi-x me-1"></i> Close Modal
                              </button>
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-sm table-hover align-middle mb-0 text-nowrap">
                                <thead className="table-light" style={{ fontSize: "12px" }}>
                                  <tr>
                                    <th className="ps-3" style={{ width: "40px" }}>No</th>
                                    <th>Pack Size</th>
                                    <th className="text-center">Current Stock</th>
                                    <th style={{ width: "150px" }}>Packets to Make</th>
                                    <th className="text-center" style={{ width: "140px" }}>Powder Needed</th>
                                    <th style={{ minWidth: "190px" }}>Packaging Material (Optional)</th>
                                    <th style={{ width: "120px" }} className="pe-3">Cover Qty (Pcs)</th>
                                  </tr>
                                </thead>
                                <tbody style={{ fontSize: "13px" }}>
                                  {distributionPackRows.map((row, idx) => {
                                    const count = parseFloat(row.packet_count) || 0;
                                    const wtKg = count * (parseFloat(row.packet_weight_kg) || 0);
                                    return (
                                      <tr key={row.pd_id || idx}>
                                        <td className="ps-3 text-muted">{idx + 1}</td>
                                        <td>
                                          <div className="fw-semibold text-dark">{row.pack_name}</div>
                                          <small className="text-muted" style={{ fontSize: "11px" }}>
                                            Weight: {(row.packet_weight_kg * 1000).toFixed(0)}g | Price: ₹{row.p_price}
                                          </small>
                                        </td>
                                        <td className="text-center">
                                          <span className="badge bg-light text-dark border">
                                            {row.current_packets} pkts
                                          </span>
                                        </td>
                                        <td>
                                          <input
                                            type="number"
                                            className="form-control form-control-sm text-center fw-semibold"
                                            placeholder="0"
                                            min="0"
                                            step="1"
                                            value={row.packet_count}
                                            onChange={(e) => handlePackCountChange(idx, e.target.value)}
                                            onWheel={(e) => e.target.blur()}
                                          />
                                        </td>
                                        <td className="text-center">
                                          <strong className={count > 0 ? "text-primary" : "text-muted"}>
                                            {wtKg.toFixed(3)} kg
                                          </strong>
                                        </td>
                                        <td>
                                          {(() => {
                                            const rowGrams = Math.round((parseFloat(row.packet_weight_kg) || 0) * 1000);
                                            const matchingMaterials = packagingMaterialsList.filter(rm => {
                                              const matWeight = extractPackagingWeightInGrams(rm.title);
                                              return matWeight && Math.abs(matWeight - rowGrams) <= 1;
                                            });
                                            const optionsList = matchingMaterials.length > 0 ? matchingMaterials : packagingMaterialsList;

                                            return (
                                              <select
                                                className="form-select form-select-sm"
                                                value={row.packaging_id || ""}
                                                onChange={(e) => handlePackagingChange(idx, e.target.value)}
                                              >
                                                <option value="">-- No Packaging Cover --</option>
                                                {optionsList.map(rm => (
                                                  <option key={rm.id} value={rm.id} disabled={parseFloat(rm.stock || 0) <= 0}>
                                                    {rm.title} (Stock: {parseFloat(rm.stock || 0).toFixed(2)} {String(rm.unit || 'kg').toLowerCase().includes('pcs') ? 'pcs' : 'kg'})
                                                  </option>
                                                ))}
                                              </select>
                                            );
                                          })()}
                                        </td>
                                        <td className="pe-3">
                                          <div className="input-group input-group-sm">
                                            <input
                                              type="number"
                                              className="form-control form-control-sm text-center"
                                              placeholder="0"
                                              min="0"
                                              step="1"
                                              disabled={!row.packaging_id}
                                              value={row.packaging_qty}
                                              onChange={(e) => handlePackagingQtyChange(idx, e.target.value)}
                                              onWheel={(e) => e.target.blur()}
                                            />
                                            {row.packaging_id && (
                                              <span className="input-group-text px-1 bg-light text-muted" style={{ fontSize: "0.75rem" }}>
                                                pcs
                                              </span>
                                            )}
                                          </div>
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Real-time Calculation Summary Card */}
                      {distributionPackRows.length > 0 && (
                        <div className="card border-0 shadow-sm mb-3" style={{ borderRadius: "10px", backgroundColor: "#f8fafc" }}>
                          <div className="card-body p-3">
                            <div className="row g-3 text-center align-items-center">
                              <div className="col-4 border-end">
                                <span className="text-muted small d-block">Available Loose Stock</span>
                                <strong className="fs-6 text-dark">{availableLooseStockKg.toFixed(3)} kg</strong>
                              </div>
                              <div className="col-4 border-end">
                                <span className="text-muted small d-block">Total Distributed</span>
                                <strong className={`fs-6 ${totalDistributedKg > availableLooseStockKg ? "text-danger" : (totalDistributedKg > 0 ? "text-success" : "text-muted")}`}>
                                  {totalDistributedKg.toFixed(3)} kg
                                </strong>
                              </div>
                              <div className="col-4">
                                <span className="text-muted small d-block">Remaining Loose Stock</span>
                                <strong className={`fs-6 ${totalDistributedKg > availableLooseStockKg ? "text-danger" : "text-primary"}`}>
                                  {(availableLooseStockKg - totalDistributedKg).toFixed(3)} kg
                                </strong>
                              </div>
                            </div>

                            {totalDistributedKg > availableLooseStockKg && (
                              <div className="alert alert-danger py-1.5 px-3 mt-3 mb-0 small text-center fw-semibold">
                                <i className="bi bi-x-circle me-1"></i>
                                Cannot distribute: Requested quantity ({totalDistributedKg.toFixed(3)} kg) exceeds available loose stock ({availableLooseStockKg.toFixed(3)} kg) by {(totalDistributedKg - availableLooseStockKg).toFixed(3)} kg!
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Modal Footer Buttons */}
                      <div className="d-flex justify-content-end align-items-center gap-2 mt-4">
                        <button
                          type="button"
                          className="btn btn-secondary px-3"
                          disabled={isDistributeSubmitting}
                          onClick={() => setShowDistributeModal(false)}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-success px-4 d-inline-flex align-items-center gap-2 fw-semibold"
                          disabled={
                            isDistributeSubmitting ||
                            totalDistributedKg <= 0 ||
                            totalDistributedKg > availableLooseStockKg ||
                            distributionPackRows.length === 0
                          }
                        >
                          {isDistributeSubmitting ? (
                            <>
                              <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                              Distributing...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-check2-circle"></i>
                              Confirm & Distribute
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bulk Upload Modal */}
        {showBulkModal && (
          <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }} tabIndex="-1">
            <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
              <div className="modal-content">
                <div className="modal-header bg-light">
                  <div className="d-flex align-items-center gap-2">
                    <FileSpreadsheet className="text-success" size={24} />
                    <h5 className="modal-title mb-0">Bulk Upload Products & Packs</h5>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkFile(null);
                      setBulkFileName("");
                      setBulkParsedData([]);
                    }}
                  ></button>
                </div>

                <div className="modal-body">
                  {/* Instructions & Template Download Banner */}
                  <div className="alert alert-info d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <strong>How it works:</strong>
                      <ul className="mb-0 ps-3 mt-1 small">
                        <li>Upload an Excel (<strong>.xlsx</strong>, <strong>.xls</strong>) or <strong>.csv</strong> file.</li>
                        <li><strong>New Products/Packs:</strong> If the product or pack doesn't exist, it will be automatically created.</li>
                        <li><strong>Existing Products/Packs:</strong> Product details (price, code, etc.) will be updated, and any quantity/costing specified will be <strong>accumulated/added to stock</strong> (similar to stock adjustments).</li>
                      </ul>
                    </div>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary d-flex align-items-center gap-1 shadow-sm text-nowrap"
                      onClick={downloadSampleTemplate}
                    >
                      <Download size={15} /> Download Sample Template
                    </button>
                  </div>

                  {/* Upload Controls Row */}
                  <div className="row g-3 mb-4 p-3 bg-light rounded border">
                    <div className="col-md-5">
                      <label className="form-label fw-bold">Stock Entry Date <span className="text-danger">*</span></label>
                      <input
                        type="date"
                        className="form-control"
                        value={bulkDate}
                        onChange={(e) => setBulkDate(e.target.value)}
                        required
                      />
                      <small className="text-muted">Date recorded on stock addition transactions & ledger.</small>
                    </div>

                    <div className="col-md-7">
                      <label className="form-label fw-bold">Select Excel / CSV File <span className="text-danger">*</span></label>
                      <input
                        type="file"
                        className="form-control"
                        accept=".xlsx, .xls, .csv"
                        onChange={handleBulkFileUpload}
                      />
                      {bulkFileName && (
                        <small className="text-success fw-bold d-block mt-1">
                          Selected: {bulkFileName} ({bulkParsedData.length} valid rows detected)
                        </small>
                      )}
                    </div>
                  </div>

                  {/* Preview Table */}
                  {bulkParsedData.length > 0 ? (
                    <div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <h6 className="mb-0 fw-bold">Data Preview ({bulkParsedData.length} items ready to upload)</h6>
                        <span className="badge bg-primary">{bulkParsedData.length} Rows</span>
                      </div>
                      <div className="table-responsive border rounded" style={{ maxHeight: "350px" }}>
                        <table className="table table-sm table-hover table-bordered mb-0 text-nowrap">
                          <thead className="table-light sticky-top">
                            <tr>
                              <th>#</th>
                              <th>Product Name</th>
                              <th>Product Code</th>
                              <th>Weight</th>
                              <th>Unit</th>
                              <th>Packets (Stock Qty)</th>
                              <th>Selling Price (INR)</th>
                              <th>Costing (INR)</th>
                              <th>Reorder Point</th>
                              <th>Box Qty</th>
                              <th>Box Price (INR)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {bulkParsedData.map((row, idx) => (
                              <tr key={idx}>
                                <td>{idx + 1}</td>
                                <td className="fw-semibold">{row.product_name}</td>
                                <td>{row.product_code || "-"}</td>
                                <td>{row.p_weight}</td>
                                <td>{row.pbox_unit}</td>
                                <td className="fw-bold text-success">+{row.qty}</td>
                                <td>₹{row.p_price}</td>
                                <td>₹{row.costing}</td>
                                <td>{row.reorder_point || "-"}</td>
                                <td>{row.pbox_qty || "-"}</td>
                                <td>{row.pbox_price ? `₹${row.pbox_price}` : "-"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-4 text-muted border rounded bg-white">
                      <FileSpreadsheet size={42} className="text-secondary mb-2 opacity-50" />
                      <p className="mb-0">Please choose an Excel (.xlsx) file or download the sample template to preview items.</p>
                    </div>
                  )}
                </div>

                <div className="modal-footer bg-light">
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setShowBulkModal(false);
                      setBulkFile(null);
                      setBulkFileName("");
                      setBulkParsedData([]);
                    }}
                    disabled={isUploadingBulk}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary d-flex align-items-center gap-1"
                    onClick={handleBulkSubmit}
                    disabled={bulkParsedData.length === 0 || isUploadingBulk}
                  >
                    <Upload size={16} />
                    {isUploadingBulk ? 'Uploading...' : `Upload & Process ${bulkParsedData.length} Items`}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Date-Wise Stock Report Modal */}
        {showReportModal && (
          <div
            className="modal fade show d-block"
            style={{ backgroundColor: "rgba(0,0,0,0.5)", zIndex: 1050 }}
            tabIndex="-1"
            role="dialog"
          >
            <div className="modal-dialog modal-xl modal-dialog-centered" style={{ maxWidth: "95%", width: "1250px" }}>
              <div className="modal-content border-0 rounded-3 shadow">
                <div className="modal-header px-4 py-3 bg-light d-flex justify-content-between align-items-center">
                  <div>
                    <h5 className="modal-title font-bold text-dark mb-0" style={{ fontFamily: "poppins" }}>
                      <i className="bi bi-journal-text text-primary me-2"></i>
                      Date-Wise Stock Report: <span className="text-primary">
                        {products.find(p => (p.id || p.product_id)?.toString() === reportProductDetail?.p_id?.toString())?.title || `Product #${reportProductDetail?.p_id}`}
                      </span>
                    </h5>
                    <small className="text-muted">
                      Pack: <strong>{reportProductDetail?.pbox_code === "FREE SIZE / LOSE QTY" ? "Lose Packs" : `${reportProductDetail?.p_weight} ${reportProductDetail?.pbox_unit}`}</strong> | Code: {reportProductDetail?.p_code || "N/A"}
                    </small>
                  </div>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={() => setShowReportModal(false)}
                  ></button>
                </div>
                <div className="modal-body p-4" style={{ fontFamily: "poppins", maxHeight: "75vh", overflowY: "auto" }}>
                  {(() => {
                    const isFreeSize = reportProductDetail?.pbox_code === "FREE SIZE / LOSE QTY";
                    const packUnitLabel = isFreeSize ? "kg" : "pkts";

                    const totalAdded = allReportTransactions
                      .filter(t => String(t.add_taken) === "1")
                      .reduce((acc, t) => acc + (parseFloat(t.qty) || 0), 0);

                    const totalTaken = allReportTransactions
                      .filter(t => String(t.add_taken) === "0")
                      .reduce((acc, t) => acc + (parseFloat(t.qty) || 0), 0);

                    const displayedTransactions = allReportTransactions.filter(item => {
                      if (reportFilter === "1") return String(item.add_taken) === "1";
                      if (reportFilter === "0") return String(item.add_taken) === "0";
                      return true;
                    });

                    return (
                      <>
                        {/* Summary Cards */}
                        <div className="row g-3 mb-4">
                          <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm bg-success bg-opacity-10 rounded-3 p-3 text-center d-flex flex-column justify-content-center">
                              <span className="text-uppercase text-muted fw-bold text-xs" style={{ fontSize: "11px" }}>Total Stock Added</span>
                              <h4 className="text-success fw-bold mb-0 mt-1 d-flex align-items-baseline justify-content-center gap-1">
                                <span>+ {formatReportQty(totalAdded, isFreeSize)}</span>
                                <small style={{ fontSize: "14px" }}>{packUnitLabel}</small>
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm bg-danger bg-opacity-10 rounded-3 p-3 text-center d-flex flex-column justify-content-center">
                              <span className="text-uppercase text-muted fw-bold text-xs" style={{ fontSize: "11px" }}>Total Stock Taken</span>
                              <h4 className="text-danger fw-bold mb-0 mt-1 d-flex align-items-baseline justify-content-center gap-1">
                                <span>- {formatReportQty(totalTaken, isFreeSize)}</span>
                                <small style={{ fontSize: "14px" }}>{packUnitLabel}</small>
                              </h4>
                            </div>
                          </div>
                          <div className="col-md-4">
                            <div className="card h-100 border-0 shadow-sm bg-primary bg-opacity-10 rounded-3 p-3 text-center d-flex flex-column justify-content-center">
                              <span className="text-uppercase text-muted fw-bold text-xs" style={{ fontSize: "11px" }}>Current Available Stock</span>
                              <h4 className="text-primary fw-bold mb-0 mt-1 d-flex align-items-baseline justify-content-center gap-1">
                                <span>{formatReportQty(reportProductDetail?.p_qty, isFreeSize)}</span>
                                <small style={{ fontSize: "14px" }}>{packUnitLabel}</small>
                                {formatAvailableStock(reportProductDetail) && !isFreeSize && (
                                  <small className="text-muted ms-1" style={{ fontSize: "13px" }}>
                                    ({formatAvailableStock(reportProductDetail)})
                                  </small>
                                )}
                              </h4>
                            </div>
                          </div>
                        </div>

                        {/* Filter Tabs & Print Button */}
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <div className="d-flex gap-2">
                            <button
                              type="button"
                              className={`btn btn-sm ${reportFilter === "" ? "btn-primary shadow-none" : "btn-outline-primary shadow-none"}`}
                              style={{ borderRadius: "6px", boxShadow: "none", outline: "none" }}
                              onClick={() => setReportFilter("")}
                            >
                              All Transactions
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${reportFilter === "1" ? "btn-success shadow-none text-white" : "btn-outline-success shadow-none"}`}
                              style={{ borderRadius: "6px", boxShadow: "none", outline: "none" }}
                              onClick={() => setReportFilter("1")}
                            >
                              <i className="bi bi-arrow-down-left-circle me-1"></i> Stock Added (In)
                            </button>
                            <button
                              type="button"
                              className={`btn btn-sm ${reportFilter === "0" ? "btn-danger shadow-none text-white" : "btn-outline-danger shadow-none"}`}
                              style={{ borderRadius: "6px", boxShadow: "none", outline: "none" }}
                              onClick={() => setReportFilter("0")}
                            >
                              <i className="bi bi-arrow-up-right-circle me-1"></i> Stock Taken (Out)
                            </button>
                          </div>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary shadow-none"
                            style={{ borderRadius: "6px", boxShadow: "none" }}
                            onClick={handlePrintReport}
                          >
                            <i className="bi bi-printer me-1"></i> Print / Export
                          </button>
                        </div>

                        {/* Printable Content Area */}
                        <div ref={reportPrintRef}>
                          {reportLoading ? (
                            <div className="text-center py-5">
                              <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                              </div>
                              <p className="text-muted mt-2">Loading transactions...</p>
                            </div>
                          ) : displayedTransactions.length === 0 ? (
                            <div className="alert alert-info text-center py-4 mb-0">
                              <i className="bi bi-info-circle fs-4 d-block mb-2"></i>
                              No transactions found for this filter view.
                            </div>
                          ) : (
                            <div className="table-responsive">
                              <table className="table table-striped table-bordered table-sm align-middle text-xs mb-0">
                                <thead className="table-light">
                                  <tr>
                                    <th>S.No</th>
                                    <th>Date & Time</th>
                                    <th>Type</th>
                                    <th>Quantity</th>
                                    <th>Unit Price</th>
                                    <th>Total Price</th>
                                    <th>Batch / Remarks</th>
                                    <th>Remaining Balance</th>
                                    <th className="text-center no-print">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {displayedTransactions.map((item, idx) => {
                                    const isAdded = String(item.add_taken) === "1";
                                    const rawDate = item.date_time || item.create_date;
                                    const formattedDate = rawDate
                                      ? new Date(rawDate).toLocaleString("en-GB", {
                                        day: "2-digit",
                                        month: "short",
                                        year: "numeric",
                                        hour: "2-digit",
                                        minute: "2-digit",
                                        hour12: true
                                      })
                                      : "-";
                                    const unitPrice = parseFloat(item.price_unit || item.mfg_price || (parseFloat(item.qty || 0) > 0 ? parseFloat(item.price || 0) / parseFloat(item.qty || 1) : 0) || 0);
                                    const totalPrice = parseFloat(item.price || (parseFloat(item.qty || 0) * unitPrice) || 0);

                                    return (
                                      <tr key={item.id || idx}>
                                        <td>{idx + 1}</td>
                                        <td className="fw-semibold">{formattedDate}</td>
                                        <td>
                                          {isAdded ? (
                                            <span className="badge bg-success text-white px-2 py-1">
                                              <i className="bi bi-plus-circle me-1"></i> Added
                                            </span>
                                          ) : (
                                            <span className="badge bg-danger text-white px-2 py-1">
                                              <i className="bi bi-dash-circle me-1"></i> Taken
                                            </span>
                                          )}
                                        </td>
                                        <td className={`fw-bold ${isAdded ? "text-success" : "text-danger"}`}>
                                          {isAdded ? `+ ${formatReportQty(item.qty, isFreeSize)}` : `- ${formatReportQty(item.qty, isFreeSize)}`} {packUnitLabel}
                                        </td>
                                        <td>₹{unitPrice.toFixed(2)}</td>
                                        <td>₹{totalPrice.toFixed(2)}</td>
                                        <td style={{ whiteSpace: "normal", wordBreak: "break-word" }}>
                                          {item.remarks ? (
                                            <span>{item.remarks}</span>
                                          ) : item.batch_no ? (
                                            <span><strong>Batch:</strong> {item.batch_no}</span>
                                          ) : (
                                            <span className="text-muted">-</span>
                                          )}
                                        </td>
                                        <td className="fw-semibold">
                                          {item.remaining != null ? `${formatReportQty(item.remaining, isFreeSize)} ${packUnitLabel}` : "-"}
                                        </td>
                                        <td className="text-center no-print">
                                          {isAdded ? (
                                            <button
                                              type="button"
                                              className="btn btn-outline-danger btn-sm py-0 px-2 d-inline-flex align-items-center gap-1 shadow-none"
                                              style={{ fontSize: "11px", height: "24px", borderRadius: "4px" }}
                                              title="Revert / Remove Added Stock"
                                              onClick={() => handleOpenRevertModal(item)}
                                            >
                                              <i className="bi bi-arrow-counterclockwise"></i> Revert
                                            </button>
                                          ) : (
                                            <span className="text-muted" style={{ fontSize: "11px" }}>-</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>
                          )}
                        </div>
                      </>
                    );
                  })()}
                </div>
                <div className="modal-footer border-t px-4 py-2 bg-light text-end">
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowReportModal(false)}
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Revert Added Stock Confirmation Modal */}
        {revertConfirmItem && (() => {
          const maxAvailable = parseFloat(revertConfirmItem.remaining != null ? revertConfirmItem.remaining : (revertConfirmItem.qty || 0));
          const isFreeSize = reportProductDetail?.pbox_code === "FREE SIZE / LOSE QTY" || reportProductDetail?.p_code === "FREE_SIZE";
          const packUnitLabel = isFreeSize ? "kg" : "pkts";
          const weightStr = (reportProductDetail?.p_weight || "1").toString().replace(/[^0-9.]/g, '');
          const packetWeight = parseFloat(weightStr || 1);
          const unitLower = (reportProductDetail?.pbox_unit || "").toLowerCase();

          const qtyNum = revertMode === "full" ? maxAvailable : (parseFloat(revertQty) || 0);

          let calculatedKg = 0;
          if (isFreeSize) {
            calculatedKg = qtyNum;
          } else if (unitLower.includes("kg") || unitLower.includes("kilo")) {
            calculatedKg = qtyNum * packetWeight;
          } else {
            calculatedKg = (qtyNum * packetWeight) / 1000;
          }

          const hasBatch = Boolean(revertConfirmItem.batch_no);

          return (
            <div
              className="modal fade show d-block"
              style={{ backgroundColor: "rgba(0,0,0,0.65)", zIndex: 1065 }}
              tabIndex="-1"
              role="dialog"
            >
              <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: "520px" }}>
                <div className="modal-content border-0 rounded-3 shadow-lg">
                  <div className="modal-header px-4 py-3 bg-danger text-white">
                    <h5 className="modal-title fw-bold mb-0 fs-6 d-flex align-items-center gap-2">
                      <i className="bi bi-arrow-counterclockwise fs-5"></i>
                      Confirm Stock & Batch Reversion
                    </h5>
                    <button
                      type="button"
                      className="btn-close btn-close-white"
                      aria-label="Close"
                      onClick={() => !revertLoading && setRevertConfirmItem(null)}
                    ></button>
                  </div>
                  <div className="modal-body p-4">
                    {/* Product & Batch Summary Card */}
                    <div className="card bg-light border-0 mb-3 rounded-3 p-3">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">Product</span>
                        <span className="fw-bold text-dark text-end">
                          {products.find(p => (p.id || p.product_id)?.toString() === reportProductDetail?.p_id?.toString())?.title || `Product #${reportProductDetail?.p_id}`}
                        </span>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <span className="text-muted small">Pack Size</span>
                        <span className="fw-semibold text-secondary">
                          {reportProductDetail?.p_weight} {reportProductDetail?.pbox_unit || ""}
                        </span>
                      </div>
                      {hasBatch && (
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <span className="text-muted small">Production Batch</span>
                          <span className="badge bg-primary px-2 py-1 fs-7">
                            {revertConfirmItem.batch_no}
                          </span>
                        </div>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="text-muted small">Available to Revert</span>
                        <span className="fw-bold text-success">
                          {maxAvailable.toFixed(isFreeSize ? 3 : 0)} {packUnitLabel}
                        </span>
                      </div>
                    </div>

                    {/* Mode selection: Full or Selected */}
                    <div className="mb-3">
                      <label className="form-label fw-bold small text-muted text-uppercase mb-2">Revert Quantity Option</label>
                      <div className="btn-group w-100" role="group">
                        <input
                          type="radio"
                          className="btn-check"
                          name="revertModeRadio"
                          id="revertFullRadio"
                          autoComplete="off"
                          checked={revertMode === "full"}
                          onChange={() => {
                            setRevertMode("full");
                            setRevertQty(String(maxAvailable));
                          }}
                        />
                        <label className={`btn btn-sm ${revertMode === "full" ? "btn-danger text-white fw-bold" : "btn-outline-secondary"}`} htmlFor="revertFullRadio">
                          Full Quantity ({maxAvailable.toFixed(isFreeSize ? 3 : 0)} {packUnitLabel})
                        </label>

                        <input
                          type="radio"
                          className="btn-check"
                          name="revertModeRadio"
                          id="revertSelectedRadio"
                          autoComplete="off"
                          checked={revertMode === "selected"}
                          onChange={() => {
                            setRevertMode("selected");
                            setRevertQty(String(maxAvailable));
                          }}
                        />
                        <label className={`btn btn-sm ${revertMode === "selected" ? "btn-danger text-white fw-bold" : "btn-outline-secondary"}`} htmlFor="revertSelectedRadio">
                          Selected Quantity
                        </label>
                      </div>
                    </div>

                    {/* Quantity input when selected mode */}
                    {revertMode === "selected" && (
                      <div className="mb-3 p-3 border rounded-3 bg-white">
                        <label className="form-label fw-semibold small mb-1">
                          Enter Quantity to Revert ({packUnitLabel}):
                        </label>
                        <div className="input-group input-group-sm">
                          <input
                            type="number"
                            className="form-control fw-bold"
                            value={revertQty}
                            min="0.01"
                            max={maxAvailable}
                            step={isFreeSize ? "0.01" : "1"}
                            placeholder={`Enter qty up to ${maxAvailable}`}
                            onChange={(e) => setRevertQty(e.target.value)}
                          />
                          <button
                            className="btn btn-outline-secondary"
                            type="button"
                            onClick={() => setRevertQty(String(maxAvailable))}
                          >
                            Max
                          </button>
                          <span className="input-group-text">{packUnitLabel}</span>
                        </div>
                        {parseFloat(revertQty) > maxAvailable && (
                          <div className="text-danger small mt-1">
                            Cannot exceed available stock of {maxAvailable} {packUnitLabel}.
                          </div>
                        )}
                        {(parseFloat(revertQty) <= 0 || isNaN(parseFloat(revertQty))) && (
                          <div className="text-danger small mt-1">
                            Please enter a valid quantity greater than 0.
                          </div>
                        )}
                      </div>
                    )}

                    {/* Dynamic Impact Summary Box */}
                    <div className="alert alert-warning text-start mb-0 py-2.5 px-3 rounded-3" style={{ fontSize: "12px" }}>
                      <div className="fw-bold mb-1 d-flex align-items-center gap-1 text-dark">
                        <i className="bi bi-info-circle-fill text-warning"></i>
                        Automated Reversion Impact:
                      </div>
                      <ul className="mb-0 ps-3 text-secondary">
                        <li>
                          <strong>{qtyNum} {packUnitLabel}</strong> will be deducted from packed inventory.
                        </li>
                        {hasBatch && (
                          <>
                            <li>
                              Bulk quantity of <strong>{calculatedKg.toFixed(3)} kg</strong> will be returned to Batch <strong>#{revertConfirmItem.batch_no}</strong> for further distribution.
                            </li>
                            <li>
                              Batch status will update to <strong>Distribute</strong> (if currently completed).
                            </li>
                            <li>
                              Packaging material (pouches/boxes) taken for these {qtyNum} {packUnitLabel} will be restored to <strong>Raw Material Stocks</strong>.
                            </li>
                          </>
                        )}
                        <li>
                          <strong>Trial Balance (General Ledger)</strong> will automatically reflect reversed values.
                        </li>
                      </ul>
                    </div>
                  </div>
                  <div className="modal-footer border-t px-4 py-2.5 bg-light d-flex justify-content-between">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm px-3"
                      disabled={revertLoading}
                      onClick={() => setRevertConfirmItem(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-sm d-flex align-items-center gap-1 px-3 shadow-sm"
                      disabled={revertLoading || qtyNum <= 0 || qtyNum > maxAvailable}
                      onClick={handleConfirmRevert}
                    >
                      {revertLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                          Reverting...
                        </>
                      ) : (
                        <>
                          <i className="bi bi-arrow-counterclockwise"></i>
                          Yes, Revert {qtyNum > 0 ? `${qtyNum} ${packUnitLabel}` : "Stock"}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

      </div>
    </div>
  )
}

export default Pod_details
